import { useEffect, useRef, useState } from "react";
import { HttpStatusCode } from "axios";

import { KeyValue } from "../../../types/keyValue";

import useErrors from "../../../libs/useErrors";

import {
  RentalStockItem,
  getStringFromRentalStockHistoryType,
  patchRentalStockRestore,
  patchRentalStockStockCount,
} from "../../../services/rentalStockServices";

import useFullScreenLoading from "../../../hooks/useFullScreenLoading";
import useSimplePopup from "../../../hooks/useSimplePopup";

import CommonLabel from "../../Label";
import CommonPopup from "../../Popup";
import HorizontalRule from "../../Rule/Horizontal";
import RadioButtonGroup from "../../Input/RadioButtonGroup";
import BottomGroup from "../../Button/Group";
import CommonButton from "../../Button";
import CommonInput from "../../Input/CommonInput";
import { RentalStockSummaryList } from "../RentalStockSummaryList";

type Props = {
  popup: string;
  item?: RentalStockItem;
  onClose: () => void;
};

export default function RentalStockChangePopup({
  popup,
  item,
  onClose,
}: Props) {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const loadingComponent = useFullScreenLoading({ isLoading });
  const [errors, { removeError, setError }] = useErrors();
  const [simplePopupComponent, { open }] = useSimplePopup({});
  const selectedRentalStockTypeItemOption = [
    {
      key: "TOTAL_ADD",
      value: getStringFromRentalStockHistoryType("TOTAL_ADD"),
    },
    {
      key: "TOTAL_MINUS",
      value: getStringFromRentalStockHistoryType("TOTAL_MINUS"),
    },
    {
      key: "RESTORED",
      value: getStringFromRentalStockHistoryType("RESTORED"),
    },
  ];

  const [selectedRentalStockTypeItem, setSelectedRentalStockTypeItem] =
    useState<KeyValue>(selectedRentalStockTypeItemOption[0]);

  const inputRef = useRef<HTMLInputElement>(null);

  const [updateCount, setUpdateCount] = useState<number>(0);

  const validation = (error?: boolean) => {
    if (!item) return false;

    if (updateCount === undefined || inputRef?.current?.value === "") {
      if (error) {
        setError("count", "알맞은 수량을 입력해주세요.");
        inputRef.current?.focus();
      }
      return false;
    }

    if (updateCount < 0) {
      if (error) {
        setError("count", "수량은 0보다 작을 수 없습니다.");
        inputRef.current?.focus();
      }
      return false;
    }

    if (selectedRentalStockTypeItem.key === "TOTAL_MINUS") {
      if (!item.stockSummary.availableCount) {
        if (error) {
          setError("count", "감소할 수 있는 대여 가능 갯수가 없습니다.");
          inputRef.current?.focus();
        }
        return false;
      } else if (item.stockSummary && item.stockSummary.availableCount) {
        if (updateCount > item.stockSummary.availableCount) {
          if (error) {
            setError(
              "count",
              item.stockSummary.availableCount > 0
                ? "최대 대여 가능 갯수보다 많습니다 (최대 " +
                    item.stockSummary.availableCount +
                    "개)"
                : "대여 가능 갯수가 없습니다."
            );
            inputRef.current?.focus();
          }
          return false;
        }
      }
    } else if (selectedRentalStockTypeItem.key === "RESTORED") {
      if (
        (item.stockSummary && item.stockSummary.lostDamagedCount) ||
        updateCount > item.stockSummary.lostDamagedCount
      ) {
        if (updateCount > item.stockSummary.lostDamagedCount) {
          if (error) {
            setError(
              "count",
              item.stockSummary.lostDamagedCount === 0
                ? "분실/손상 복원 가능한 갯수가 없습니다."
                : "최대 분실/손상 복원 갯수보다 많습니다 (최대 " +
                    item.stockSummary.lostDamagedCount +
                    "개)"
            );
            inputRef.current?.focus();
          }
          return false;
        }
      }
    }

    return true;
  };

  const handleConfirmApply = async () => {
    if (!item) return;

    if (!validation(true)) return;

    setIsLoading(true);
    try {
      let result;
      if (
        selectedRentalStockTypeItem.key === "TOTAL_ADD" ||
        selectedRentalStockTypeItem.key === "TOTAL_MINUS"
      ) {
        result = await patchRentalStockStockCount(
          item.stockId,
          selectedRentalStockTypeItem.key === "TOTAL_MINUS"
            ? -updateCount
            : updateCount
        );
      } else if (selectedRentalStockTypeItem.key === "RESTORED") {
        result = await patchRentalStockRestore(item.stockId, updateCount);
      }

      if (result && result.status === HttpStatusCode.Ok) {
        onClose();
        //if success
        open("재고 변경이 완료되었습니다.");
      }
    } catch (e: any) {
      if (e?.response?.data?.error) {
        open(e.response.data.error?.replace(". ", ".\n"));
      } else {
        open("재고 변경에 실패했습니다.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (item) {
      removeError("count");
      setSelectedRentalStockTypeItem(selectedRentalStockTypeItemOption[0]);
      setUpdateCount(0);
    }
  }, [item, popup]);

  return (
    <>
      {loadingComponent}
      <CommonPopup
        title="재고 변경"
        isOpen={popup === "rentalStockChange"}
        onClose={onClose}
        unUseHorizonRule
        padding={0}
      >
        <div className="px-5">
          <div className="flex flex-col p-2.5 text-xl text-center font-medium rounded-[10px] bg-brand-neutral-05">
            <div className="truncate">{item?.goodsName}</div>
          </div>
          <div className="flex flex-col">
            {item?.stockSummary && (
              <RentalStockSummaryList summary={item.stockSummary} />
            )}
          </div>
        </div>
        <HorizontalRule margin="0" className="bg-brand-neutral-05 h-[6px]" />
        <div className="px-5 py-5 flex flex-col gap-2.5">
          <div className="flex flex-col">
            <CommonLabel label="변경 구분" required />
            <RadioButtonGroup
              variantActive="select"
              innerClassName="min-w-[36px] design:text-sm"
              items={selectedRentalStockTypeItemOption}
              value={selectedRentalStockTypeItem}
              onChange={(selectedItem) => {
                removeError("count");
                setSelectedRentalStockTypeItem(selectedItem);
                setUpdateCount(0);
              }}
            />
          </div>
          <div className="flex flex-col">
            <CommonLabel label="변경 수량" required />
            <CommonInput
              placeholder="변경할 수량을 입력해주세요"
              type="number"
              pattern="[0-9]*"
              maxLength={10}
              onChange={(value) => {
                removeError("count");
                if (isNaN(Number(value))) return;
                setUpdateCount(Number(value));
              }}
              value={
                updateCount === undefined
                  ? ""
                  : updateCount === 0
                  ? ""
                  : updateCount.toString()
              }
              ref={inputRef}
              error={errors["count"]}
            />
          </div>
        </div>
        <div className="pb-[50px]"></div>
        <BottomGroup zIndex="high">
          <div className="flex flex-row gap-[6px] h-bottom-button">
            <CommonButton
              variant="grey"
              className="w-[100px] rounded-none"
              onClick={onClose}
            >
              취소
            </CommonButton>
            <CommonButton
              disabled={isLoading}
              validation={!validation()}
              className="w-full rounded-none"
              variant="primary"
              onClick={handleConfirmApply}
            >
              확인
            </CommonButton>
          </div>
        </BottomGroup>
      </CommonPopup>
      {simplePopupComponent}
    </>
  );
}
